import '@bpd-library/components/dist/molecules/map/map';
import '@features/compare/components/compare-open-button';
import '@molecules/apartments-view';

import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { useStore } from '@atomify/kit';
import { Button, ButtonInterface } from '@atoms/button/button';
import { getMapInstance, MapState, mapStore } from '@bpd-library/components';
import { BoundingBox } from '@bpd-library/types';
import { ExtendedConstructionNumber, ExtendedPropertyType } from '@bpd-library/utilities';
import { getMapCardTitle } from '@features/area-map/utilities/get-map-card-title';
import {
    RoutePerObjectHandler,
    usePropertiesRouting,
} from '@features/properties/hooks/use-properties-routing';
import {
    getCurrentView,
    getHouseSelectorData,
    PropertiesState,
    propertiesStore,
    View,
    ViewType,
} from '@features/properties/store';
import {
    findInPropertiesDataById,
    setPolygonDrawingOptions,
    sortHouseSelectorData,
} from '@features/properties/utilities/data';
import { PROPERTIES_THEME } from '@features/properties/utilities/extend-map-theme';
import { resetFilters } from '@source/store/filters';
import { GTMPush } from '@source/utilities/gtm';
import { useTranslate } from '@source/utilities/hooks';
import { getDebuggerMapOptions } from '@store/debugger';
import { hasConsent, ServiceConsentState, serviceConsentStore } from '@store/service-consent';
import {
    formatConstructionNumberStatus,
    formatDetailPageUrl,
    formatLivingArea,
    formatPlotArea,
    formatPrice,
    formatPropertyTypeStatus,
    formatRooms,
    FormattedPrice,
    FormattedStatus,
} from '@utilities/api-formatters';
import { isConstructionNumberNhg } from '@utilities/api-utilities/is-nhg';
import { handleGTMPolygonInteraction, PolygonDimensionality } from '@utilities/gtm-map';
import { isMobile } from '@utilities/media-query';
import mapboxgl from 'mapbox-gl';

import { HouseSelectorElement } from '../house-selector';
import { HouseSelectorCard } from '../house-selector-card';
import { HouseSelectorObjectHandler, HouseSelectorView } from '../house-selector/utilities/types';
import { useContentInteraction } from './hooks/use-content-interaction';
import { useVisualInteraction } from './hooks/use-visual-interaction';
import { sortData } from './utilities/sorting';
import { getTiles } from './utilities/tiles';

const APARTMENT_CLASS = 'properties-view--apartments';
const HOUSE_SELECTOR_CLASS = 'properties-view--house-selector';
const EXPANDED_MAP_CLASS = 'properties-view--expanded-map';
const FULLSCREEN_CLASS = 'properties-view--fullscreen';
const NO_RESULTS_CLASS = 'properties-view--no-results';
const RESIZING_CLASS = 'properties-view--resizing';
const HIDDEN_CLASS = 'u-hidden';

const SWITCH_DEFAULT_PROPS: ButtonInterface = {
    size: 'small',
    width: 'square',
};

const SWITCH_2D3D_ACTIVE_PROPS: ButtonInterface = {
    ...SWITCH_DEFAULT_PROPS,
    shape: 'smooth-15',
    variant: 'grey-90',
};

const SWITCH_2D3D_INACTIVE_PROPS: ButtonInterface = {
    ...SWITCH_DEFAULT_PROPS,
    shape: 'smooth-15',
    variant: 'white',
};

const SWITCH_ZOOM_ACTIVE_PROPS: ButtonInterface = {
    ...SWITCH_DEFAULT_PROPS,
    size: 'regular',
    variant: 'white',
};

type ViewMode = '2D' | '3D';

export interface PropertiesViewElement extends Component {
    viewMode: ViewMode;
    fullScreen: boolean;
    renderCardPopup: (object: ExtendedPropertyType | ExtendedConstructionNumber) => Promise<void>;
}

const PropertiesView: FC<PropertiesViewElement> = ({ element }) => {
    const [layerRaster] = useProp<string | null>('layerRaster', null);
    const [boundingBox] = useProp<BoundingBox | null>('boundingBox', null);
    const [, setFullScreen, watchFullScreen] = useProp<boolean>('fullScreen', false);
    const [, setViewMode, watchViewMode] = useProp<ViewMode>('viewMode', '2D');
    const [hasHouseSelector] = useProp<boolean>('hasHouseSelector', false);
    const [onRouteRequested] = useProp<RoutePerObjectHandler>('onRouteRequested');

    const tiles = useElement<HTMLDivElement>('[js-hook-properties-view-tiles]');
    const mapContainer = useElement<HTMLDivElement>('[js-hook-properties-map-container]');
    const scrollContainer = useElement<HTMLDivElement>(
        '[js-hook-properties-view-scroll-container]',
    );
    const viewModeToggle = useElement<HTMLDivElement>('[js-hook-2d-3d-toggle]');
    const mapZoomToggle = useElement<HTMLDivElement>('[js-hook-map-zoom-toggle]');
    const fullscreenToggle = useElement<HTMLDivElement>('[js-hook-fullscreen-toggle-button]');
    const houseSelectorElement = useElement<HouseSelectorElement>('bpd-house-selector');
    const headerTotal = useElement<HTMLDivElement>('[js-hook-properties-view-total]');
    const cardPopup = useElement<HTMLDivElement>('[js-hook-card-popup]');

    const [, subscribe] = useStore<PropertiesState>(propertiesStore);
    const [, subscribeMap] = useStore<MapState>(mapStore);
    const [, subscribeConsentStore] = useStore<ServiceConsentState>(serviceConsentStore);

    useVisualInteraction({ current: element }, scrollContainer);
    const [onMouseenter, onMouseleave] = useContentInteraction({ houseSelectorElement });

    const { updateCurrentRoute } = usePropertiesRouting();

    let currentView: View | undefined;

    onDidLoad(async () => {
        if (hasHouseSelector) {
            await houseSelectorElement.current?.componentOnReady();
            setViewMode('3D');
        }

        handleConsent(hasConsent('mapbox'));

        handleView();
        renderFullscreenToggleButton();
    });

    subscribe(handleView, ['view']);

    watchViewMode((viewMode) => {
        renderViewModeToggle(viewMode);

        const action = viewMode === '3D' ? 'add' : 'remove';
        element.classList[action](HOUSE_SELECTOR_CLASS);
    });

    watchFullScreen((fullScreen) => {
        closeCardPopup();
        const action = fullScreen ? 'add' : 'remove';
        element.classList[action](FULLSCREEN_CLASS);

        // hide map in css transition
        toggleViewportResizing(true);

        // do resize
        if (isMobile()) {
            setTimeout(() => {
                element.classList[action](EXPANDED_MAP_CLASS);
                initResizeEvent();
            }, 500); // 500 opacity transition
        } else {
            setTimeout(() => {
                initResizeEvent();
            }, 700 + 500); // 700 animation time, 500 opacity transition
            element.classList[action](EXPANDED_MAP_CLASS);
        }
    });

    useBindMethod('renderCardPopup', renderPropertiesViewCardPopup);

    const renderViewModeToggle = (viewMode: ViewMode) => {
        if (!viewModeToggle.current) return;
        viewModeToggle.current.innerHTML = '';
        viewModeToggle.current.append(
            <button-toggle
                classes="properties-view__control properties-view__control-switch properties-view__2d3d-toggle"
                type="switch"
                toggleOff={{
                    label: '2D',
                    onClick: () => {
                        setViewMode('2D');
                    },
                    ...(viewMode === '2D' ? SWITCH_2D3D_ACTIVE_PROPS : SWITCH_2D3D_INACTIVE_PROPS),
                }}
                toggleOn={{
                    label: '3D',
                    onClick: () => {
                        setViewMode('3D');
                    },
                    ...(viewMode === '3D' ? SWITCH_2D3D_ACTIVE_PROPS : SWITCH_2D3D_INACTIVE_PROPS),
                }}
                is-toggle-on={viewMode === '3D'}
            />,
        );
    };

    const renderMapZoomToggle = (map: mapboxgl.Map) => {
        if (!mapZoomToggle.current) return;
        mapZoomToggle.current.innerHTML = '';
        mapZoomToggle.current.append(
            <button-toggle
                classes="properties-view__control properties-view__control-switch properties-view__map-zoom-control"
                direction="column"
                type="switch"
                toggleOff={{
                    ...SWITCH_ZOOM_ACTIVE_PROPS,
                    icon: 'icons-plus',
                    onClick: () => {
                        GTMPush({
                            event: 'Map Interaction',
                            eventCategory: 'Map',
                            eventAction: 'Zoom filter',
                            eventLabel: 'zoom_in',
                        });
                        map.zoomIn();
                    },
                    classes: 'properties-view__map-zoom-control--plus',
                }}
                toggleOn={{
                    ...SWITCH_ZOOM_ACTIVE_PROPS,
                    icon: 'icons-minus',
                    onClick: () => {
                        GTMPush({
                            event: 'Map Interaction',
                            eventCategory: 'Map',
                            eventAction: 'Zoom filter',
                            eventLabel: 'zoom_out',
                        });
                        map.zoomOut();
                    },
                }}
            />,
        );
    };

    function renderFullscreenToggleButton() {
        fullscreenToggle.current!.innerHTML = '';
        fullscreenToggle.current!.append(
            <button-toggle
                classes="properties-view__control properties-view__map-expand"
                toggleOff={{
                    variant: 'grey-90',
                    shape: 'smooth-32',
                    icon: 'icons-enlarge',
                }}
                toggleOn={{
                    variant: 'grey-90',
                    shape: 'smooth-32',
                    icon: 'icons-close',
                    ...(!isMobile() && {
                        label: useTranslate('PropertiesOverview-MapCollapse'),
                    }),
                }}
                is-toggle-on={element.fullScreen}
                onClick={() => {
                    setFullScreen(!element.fullScreen);
                }}
            />,
        );
    }

    async function renderPropertiesViewCardPopup(
        object: ExtendedPropertyType | ExtendedConstructionNumber,
    ) {
        let formattedStatus: FormattedStatus | undefined;
        let formattedPrice: FormattedPrice | undefined;
        let url: string | undefined;
        let identifier: string | null | undefined;
        let isNhg: boolean | undefined;

        switch (object.objectType) {
            case 'propertyType':
                formattedStatus = formatPropertyTypeStatus(object);
                url = formatDetailPageUrl({ propertyType: object });
                formattedPrice = formatPrice({ propertyType: object });

                break;
            case 'constructionNumber':
                formattedStatus = formatConstructionNumberStatus(object);
                url = formatDetailPageUrl({ constructionNumber: object });
                formattedPrice = formatPrice({ constructionNumber: object });
                identifier = object.identifier;
                isNhg = isConstructionNumberNhg(object);

                break;
            default:
                break;
        }

        const livingArea = formatLivingArea(object);
        const plotArea = formatPlotArea(object);
        const rooms = formatRooms(object);
        const title = await getMapCardTitle(object);
        const projectPhaseName = object.projectPhaseName;

        const specs = [];

        if (livingArea) {
            specs.push({
                title: `${livingArea.value} ${livingArea.suffix || ''}`,
                subtitle: useTranslate('houses-infobox-living'),
            });
        }
        if (plotArea) {
            specs.push({
                title: `${plotArea.value} ${plotArea.suffix || ''}`,
                subtitle: useTranslate('houses-infobox-lot'),
            });
        }
        if (rooms) {
            specs.push({
                title: `${rooms.value}`,
                subtitle: useTranslate('Rooms'),
            });
        }

        cardPopup.current!.innerHTML = '';
        cardPopup.current!.append(
            <HouseSelectorCard
                title={title}
                status={formattedStatus}
                price={formattedPrice}
                description={projectPhaseName}
                identifier={identifier}
                image={object.mainImage?.url}
                buttonLabel={useTranslate('ViewDetails')}
                buttonUrl={url}
                specs={specs}
                onClose={closeCardPopup}
                isNhg={isNhg}
            />,
        );
        cardPopup.current?.classList.remove(HIDDEN_CLASS);
    }

    function closeCardPopup() {
        cardPopup.current?.classList.add(HIDDEN_CLASS);
        cardPopup.current!.innerHTML = '';
    }

    function handleConsent(consent: boolean) {
        if (consent) {
            renderMap();
        } else {
            renderSDPBanner();
        }
    }

    function handleView() {
        currentView = getCurrentView();

        headerTotal.current!.innerHTML = '';
        tiles.current!.innerHTML = '';

        if (currentView) {
            sortData(currentView);
            resizeMap();
            setViewTypeClass(currentView.type);
            setHouseSelectorView(currentView);

            if (currentView.data.length && currentView.dataType !== 'recommended') {
                setHeaderTotal();
                renderTiles();
            } else {
                renderNoResults();
            }
        }
    }

    subscribeMap(({ mapInstance: map }) => map && renderMapZoomToggle(map), ['mapInstance']);

    subscribeMap(
        ({ click }) => {
            if (!click?.id) return;
            handlePropertiesViewObjectClick(click.id, { dimensionality: '2D' });
        },
        ['click'],
    );

    subscribeConsentStore(
        ({ mapbox }) => {
            handleConsent(mapbox);
            updateCurrentRoute();
        },
        ['mapbox'],
    );

    const handleHouseSelectorObjectClick: HouseSelectorObjectHandler = ({
        objectId,
        linkToViewId: viewId,
    }) => {
        if (!objectId) return;
        handlePropertiesViewObjectClick(objectId, { viewId, dimensionality: '3D' });
    };

    const handlePropertiesViewObjectClick = (
        objectId: string,
        options?: { viewId?: string | null; dimensionality?: PolygonDimensionality },
    ) => {
        closeCardPopup();

        const { object, isSoldProjectData } = findInPropertiesDataById(objectId);
        if (!object) return;

        const { viewId, dimensionality } = options || {};
        onRouteRequested(object, {
            isSoldProjectData,
            viewId,
            isFullscreen: element.fullScreen,
        });

        handleGTMPolygonInteraction(object, dimensionality);
    };

    function setViewTypeClass(type: ViewType) {
        const isApartmentView = type === 'apartmentConstructionNumber';
        element.classList[isApartmentView ? 'add' : 'remove'](APARTMENT_CLASS);
    }

    const hasCurrentViewDataObjects = (view: HouseSelectorView, currentView: View) => {
        return !!view.polygons?.find(({ objectId }) =>
            currentView.data.find(({ id }) => id === objectId),
        );
    };

    function setHouseSelectorView(currentView: View) {
        const houseSelectorData = getHouseSelectorData();
        if (!houseSelectorData || !houseSelectorElement.current) return;

        const { views } = houseSelectorData ?? {};

        if (!views?.length) return;
        views.forEach((view) =>
            view.polygons?.forEach((polygon) => setPolygonDrawingOptions(polygon, currentView)),
        );
        sortHouseSelectorData(houseSelectorData);

        let nextView: HouseSelectorView | undefined;

        if (currentView.viewId) {
            nextView = views.find((view) => view.id === currentView.viewId);
        }

        if (
            currentView.type !== 'propertyType' &&
            currentView.type !== 'apartmentPropertyType' &&
            !nextView
        ) {
            nextView = views.find((view) => hasCurrentViewDataObjects(view, currentView));
        }

        if (!nextView) nextView = views[0];
        houseSelectorElement.current.setView(nextView);
    }

    function renderTiles() {
        const tileElements = getTiles(onMouseenter, onMouseleave);

        if (tileElements) {
            tiles.current!.append(tileElements);
            element.classList.remove(NO_RESULTS_CLASS);
        }
    }

    function renderNoResults() {
        element.classList.add(NO_RESULTS_CLASS);

        tiles.current!.innerHTML = '';
        tiles.current!.append(
            <div class="properties-view__no-results">
                <div class="properties-view__no-results-inner">
                    <bpd-svg name="icons-wishlist-empty" />

                    <h2 class="properties-view__no-results-title">
                        {useTranslate('HousingOverview.NoResultTitle')}
                    </h2>
                    <p class="properties-view__no-results-description">
                        {useTranslate('HousingOverview.NoResultDescription')}
                    </p>
                    <Button
                        label={useTranslate('HousingOverview.ResetFilter')}
                        onClick={() => {
                            GTMPush({
                                event: 'gtm.filters',
                                eventCategory: 'Woonwensen',
                                eventAction: 'Reset filters',
                            });

                            resetFilters(true);
                        }}
                    />
                </div>
            </div>,
        );
    }

    function renderMap() {
        mapContainer.current!.innerHTML = '';
        mapContainer.current!.append(
            <bpd-map
                options={{
                    boundingBox,
                    layerRaster,
                    autoOpenUrl: false,
                    ...getDebuggerMapOptions(),
                }}
                theme={PROPERTIES_THEME}
            />,
        );
    }

    function renderSDPBanner() {
        mapContainer.current!.innerHTML = '';
        mapContainer.current!.append(<div uc-mapbox-banner />);
    }

    function initResizeEvent() {
        const resizeEvent = new CustomEvent('properties-view::map-resized');
        document.dispatchEvent(resizeEvent);
        resizeMap();
        // show map again
        toggleViewportResizing(false);
    }

    function resizeMap() {
        if (!hasConsent('mapbox')) return;
        requestAnimationFrame(() => getMapInstance()?.resize());
    }

    function toggleViewportResizing(isResizing: boolean) {
        const action = isResizing ? 'add' : 'remove';
        element.classList[action](RESIZING_CLASS);
    }

    function setHeaderTotal() {
        const { type, dataType } = currentView || {};
        const hasPropertyTypes = type === 'apartmentPropertyType' || type === 'propertyType';
        const total = currentView?.data.length;
        let titleSingular = hasPropertyTypes
            ? useTranslate('HouseType')
            : useTranslate('BuildingNumber');
        let titlePlural = hasPropertyTypes
            ? useTranslate('HouseTypes')
            : useTranslate('HousingOverview-ConstructionNumbers');

        if (dataType === 'soldProjects') {
            titleSingular = useTranslate('ProjectPhase.soldInArea');
            titlePlural = useTranslate('ProjectPhases.soldInArea');
        }

        if (total) {
            headerTotal.current!.innerHTML = '';
            headerTotal.current!.append(
                <Fragment>
                    <span class="u-h4 u-h4--md">{total}</span>
                    <span class="u-h4 u-h4--md">{total > 1 ? titlePlural : titleSingular}</span>
                </Fragment>,
            );
        }
    }

    return (
        <div class="properties-view__container">
            <div class="properties-view__content" js-hook-properties-view-scroll-container>
                <div class="properties-view__header-total" js-hook-properties-view-total />

                <div class="properties-view__tiles" js-hook-properties-view-tiles />

                <div class="properties-view__compare">
                    <compare-open-button />
                </div>
            </div>

            <div class="properties-view__visual">
                <div class="properties-view__map" js-hook-properties-map-container></div>

                <div class="properties-view__apartments">
                    <apartments-view vr-tour-id="properties-view-tour" expanded />
                </div>

                {hasHouseSelector ? (
                    <div class="properties-view__house-selector" js-hook-house-selector-container>
                        <bpd-house-selector onObjectClick={handleHouseSelectorObjectClick} />
                    </div>
                ) : null}

                <div class="properties-view__card-popup" js-hook-card-popup />

                <div class="properties-view__controls">
                    <div js-hook-fullscreen-toggle-button />

                    <div js-hook-2d-3d-toggle />

                    <div js-hook-map-zoom-toggle />
                </div>
            </div>
        </div>
    );
};

PropertiesView.props = {
    viewMode: {
        type: String,
    },
    layerRaster: {
        type: String,
        required: true,
    },
    boundingBox: {
        type: Array,
    },
    fullScreen: {
        type: Boolean,
        reflectToAttr: true,
    },
    hasHouseSelector: {
        type: Boolean,
    },
    onRouteRequested: {
        type: Function,
        required: true,
    },
};

defineElement('properties-view', PropertiesView);
